import { Team } from './teams';
import { Image } from './common';
import { ApiClient } from '@dozuki/web-js/utils';
import { makeRequest, CommonRequestOptions } from './makeRequest';
import { createErrorMessageTypeGuard } from './createErrorMessageTypeGuard';

export type User = {
   username: string;
   unique_username: string;
   login: string;
   userid: number;
   uid: string;
   uid_v2: string;
   image: Image;
   userAvatarUrl: string;
   hasCustomAvatar: boolean;
   reputation: number;
   teams: number[];
   isUserAnyTeamOwner: boolean;
   join_date: number;
   certification_count: number;
   badge_counts: {
      bronze: number;
      silver: number;
      gold: number;
      total: number;
   };
   summary: string;
   about_raw: string;
   about_rendered: string;
   langid: string;
   machine_translation: boolean;
   authToken: string;
   permissions: UserPermissions;
   periodicReviewTotals: {
      hasOverduePeriodicReviews: boolean;
      numberOfPeriodicReviewRequests: number;
      numberOfUpcomingPeriodicReviews: number;
   } | null;
   siteid: number;
   sitename: string;
   siteUid: string;
   email?: string;
   shouldUseGuideStepEditorV2?: boolean;
   privileges: string[];
};

export type UserPermissions = Record<
   | 'canIViewCoursesAdminIndex'
   | 'canIAssignCoursesAdminIndex'
   | 'canIEditCoursesAdminIndex'
   | 'canIManageCoursesAdminIndex'
   | 'canICreateNewWikis'
   | 'canIManageSupport'
   | 'canIViewDropdownManageTab'
   | 'canIViewSkillsMatrix'
   | 'canIEditSkillWeights'
   | 'canISetAnySupervisorRatings'
   | 'canIViewAnyCourseSkillsMatrix'
   | 'canIViewSiteReports'
   | 'canIEditAnyUsersProfile'
   | 'canIManageExternalTrainingModules'
   | 'canIViewAllQuizzes'
   | 'canIViewAllQuizSessions'
   | 'canIViewAppMarketplace'
   | 'canIViewAuditHistory'
   | 'canIViewCreatorPro'
   | 'canIViewQuizLibrary'
   | 'canIBulkCancelQuizSessions'
   | 'canIGenerateQRCode'
   | 'canIManageDashboards'
   | 'canIAuthorQuizzes',
   boolean
>;

export async function getLoggedInUser(common: CommonRequestOptions = {}): Promise<User | null> {
   try {
      return await makeRequest('get', 'user', {
         ...common,
      });
   } catch (e) {
      // Don't assume the page requires an authenticated user
      if (ApiClient.isInvalidLoginError(e)) {
         return null;
      }
      throw e;
   }
}

export async function getUser(userid: number, common: CommonRequestOptions = {}): Promise<User> {
   return makeRequest('get', `users/${userid}`, common);
}

export async function getUserByUID(
   userUID: string,
   common: CommonRequestOptions = {}
): Promise<User> {
   return makeRequest('get', `users/searchbyuid/${userUID}`, common);
}

type GetUserErrorCode = 'users.user_not_found';
getUser.errorMessages = createErrorMessageTypeGuard<GetUserErrorCode>();

export type GetUsersSortOption = 'ASC' | 'DESC';

export type GetUsersOptions = CommonRequestOptions & {
   limit?: number;
   offset?: number;
   order?: GetUsersSortOption;
   joinedSince?: number;
};

export function getUsers({
   limit,
   offset,
   order,
   joinedSince,
   ...common
}: GetUsersOptions = {}): Promise<User[]> {
   return makeRequest('get', 'users', {
      ...common,
      query: {
         limit,
         offset,
         order,
         joinedSince,
      },
   });
}

type GetUsersErrorCode = 'users.list.insufficient_permissions';
getUsers.errorMessages = createErrorMessageTypeGuard<GetUsersErrorCode>();

export type SearchUsersOptions = CommonRequestOptions & {
   limit?: number;
   page?: number;
   uniqueOnly?: boolean;
};

export type SearchUsersResult = {
   results: User[];
   totalResults: number;
   moreResults: boolean;
   limit: number;
   search: string;
};

export async function searchUsers(
   searchTerm: string,
   { limit, page, uniqueOnly, ...common }: SearchUsersOptions = {}
): Promise<SearchUsersResult> {
   return makeRequest('get', `users/search/${encodeURIComponent(searchTerm)}`, {
      ...common,
      query: {
         limit,
         page,
         uniqueOnly,
      },
   });
}
